import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import ModalFooterWithActions from '../../../../../../components/modal-footer-actions';
import ModalPortal from '../../../../../../components/modal-portal';

import { verifyEmailAddress } from '../../../../api/store-api';
import { messages } from './messages';

import style from '../style.module.less';

const MODAL_HEADER_ID = 'verify-email-modal-header';

const VerifyEmailModal = ({ email, onClose, getEmailVerificationStatus }) => {
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const sendEmail = useCallback(async () => {
    setIsLoading(true);
    verifyEmailAddress(email).then(
      () => {
        setIsLoading(false);
        getEmailVerificationStatus(email);
        onClose();
      },
      () => {
        setIsLoading(false);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.header} />,
          content: <FormattedMessage {...messages.error} />,
          duration: 3000,
        });
      },
    );
  }, [email, onClose, setIsLoading, getEmailVerificationStatus]);
  return (
    <ModalPortal containerId='verify-email-modal' onClose={onClose}>
      <Modal modalHeaderId={MODAL_HEADER_ID} size='medium'>
        <ModalHeader closeModal={onClose} id={MODAL_HEADER_ID}>
          <FormattedMessage {...messages.header} />
        </ModalHeader>
        <ModalBody className={style['email-modal-body']}>
          {isLoading && <LoadingIndicator className={style['loading-indicator']} theme='grey' />}
          {!isLoading && (
            <>
              <p>
                <FormattedMessage {...messages.line1} />
              </p>
              <p>
                <FormattedMessage {...messages.line2} />
              </p>
              <p>
                <b>
                  <FormattedMessage {...messages.line3} />
                </b>
              </p>
              <p>
                <FormattedMessage {...messages.line4} values={{ email, b: (chunks) => <b>{chunks}</b> }} />
              </p>
            </>
          )}
        </ModalBody>
        {!isLoading && (
          <ModalFooterWithActions
            actionMessage={<FormattedMessage {...messages.sendConfirmation} />}
            actionTheme='primary'
            onAction={sendEmail}
          />
        )}
      </Modal>
    </ModalPortal>
  );
};

VerifyEmailModal.propTypes = {
  email: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  getEmailVerificationStatus: PropTypes.func.isRequired,
};

export default VerifyEmailModal;
